import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import dayjs from 'dayjs';

import { useModalManager } from '../../../../../hooks/ModalManager.hook';
import { useAppDispatch } from '../../../../../hooks/useAppDispatcher.hook';

import {
  getHRControlListThunk,
  updateHRControlThunk,
} from '../../../../../redux/ControlPanel/HRControl/HRControl.thunk';

import CardComponent from '../../../../../components/Card.component';
import InputComponent from '../../../../../components/Input/Input.component';
import SpinnerComponent from '../../../../../components/Spinner.component';
import ControlCreateEditModal, {
  ControlPanelItemFormValues,
} from '../../../../../components/Modal/Control/ControlCreateEditModal';
import ConfirmDuplicateModal from '../../../../../components/Modal/Control/ConfirmDuplicateModal';
import ControlSectionContent from '../ControlSectionContent.component';
import ControlHRItem from './ControlHRItem.component';
import { SortTypes } from '../../../../../types/sort.types';

import SearchIcon from '../../../../../components/Icons/Search.icon';
import SortArrowsIcon from '../../../../../components/Icons/SortArrows.icon';

import { ControlSectionTypes } from '../../../../../redux/ControlPanel/ControlPanelList/ControlPanelList.type';
import { checkUserRequiredPermissions } from '../../../../../utils/getUserRolesByToken';
import { UserPermissions } from '../../../../../types/userPermissions.types';
import useAuthHook from '../../../../../hooks/useAuth.hook';
import { CAN_ALL_USERS_SEE_HR_REGIONS } from '../../constants/constants';
import { useSelector } from 'react-redux';
import {
  getHRControlList,
  getHRControlListPagination,
  setHrControlList,
} from '../../../../../redux/ControlPanel/HRControl/HRControl.slice';
import {
  StyledControlListItem,
  StyledControlSectionContainer,
  StyledControlSectionHeader,
  StyledHeaderLabel,
  StyledHeaderTitle,
  StyledSearchIcon,
  StyledSearchWrapper,
  StyledSortContainer,
} from '../sharedStyledComponents';
import { candidateVisibilityService } from '../../../../../redux/ControlPanel/CandidateVisibility/CandidateVisibility.service';
import { IControlDataItem } from '../../ControlPanel.types';

const sectionType = ControlSectionTypes.HRS;

export const HrsSectionComponent: FC = () => {
  const { user } = useAuthHook();
  const dispatch = useAppDispatch();
  const { openModal } = useModalManager();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [sort, setSort] = useState(SortTypes.ASC);
  const [visibilityByEmployeeId, setVisibilityByEmployeeId] = useState<
    Record<number, string>
  >({});

  const hrs = useSelector(getHRControlList);
  const pagination = useSelector(getHRControlListPagination);

  const editable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_ASSIGN_REGIONS_TO_HR,
  ]);

  const canManageCandidateVisibility = checkUserRequiredPermissions(
    user.permissions,
    [UserPermissions.CAN_MANAGE_CANDIDATE_VISIBILITY],
  );

  const deletable = false;
  const viewable = CAN_ALL_USERS_SEE_HR_REGIONS;

  const fetchParams = useMemo(() => {
    return {
      q: searchQuery,
      sort: `${sort === SortTypes.DESC ? '-' : ''}name`,
    };
  }, [sort, searchQuery]);

  const loadVisibilitySettings = useCallback(async () => {
    if (!canManageCandidateVisibility) {
      return;
    }

    try {
      const response = await candidateVisibilityService.getList({ perPage: 500 });
      const map: Record<number, string> = {};

      response.data.forEach(setting => {
        if (setting.hide_candidates_before) {
          map[setting.employee_id] = setting.hide_candidates_before;
        }
      });

      setVisibilityByEmployeeId(map);
    } catch {
      setVisibilityByEmployeeId({});
    }
  }, [canManageCandidateVisibility]);

  const enrichHrItem = useCallback(
    (item: IControlDataItem): IControlDataItem => {
      const hideDate = visibilityByEmployeeId[item.id];

      return {
        ...item,
        hideCandidatesBefore: hideDate ? dayjs(hideDate).valueOf() : null,
      };
    },
    [visibilityByEmployeeId],
  );

  const createItemWithModalWrapper = async (
    fetchFunc: Function,
    updateFunc: Function,
    payload: any,
    updateFetchParams?: any,
    message?: string,
  ) => {
    dispatch(fetchFunc(payload)).then((res: any) => {
      if (!res.payload && !!res.error) {
        openModal(ConfirmDuplicateModal, {
          message: message ?? t(`Such a record already exists!`),
        });
      } else {
        dispatch(updateFunc(updateFetchParams));
      }
    });
  };

  const editSectionItem = (sectionItem: ControlPanelItemFormValues) => {
    createItemWithModalWrapper(
      updateHRControlThunk,
      getHRControlListThunk,
      {
        id: sectionItem.id,
        regions: sectionItem.regions?.map(r => r.value),
      },
      fetchParams,
    );
  };

  const saveVisibility = async (
    employeeId: number,
    hideCandidatesBefore: number | null,
    initialHideCandidatesBefore: number | null,
  ) => {
    const hadRestriction = initialHideCandidatesBefore != null;
    const hasRestriction = hideCandidatesBefore != null;

    if (!hasRestriction && hadRestriction) {
      await candidateVisibilityService.destroy(employeeId);
    } else if (hasRestriction) {
      await candidateVisibilityService.update({
        employee_id: employeeId,
        hide_candidates_before: dayjs(hideCandidatesBefore).format('YYYY-MM-DD'),
      });
    }

    await loadVisibilitySettings();
  };

  const handleSortChange = (sortOrder: SortTypes) => {
    handleSort(sortOrder);

    dispatch(
      getHRControlListThunk({
        page: 1,
        q: searchQuery,
        sort: `${sortOrder === SortTypes.ASC ? '-' : ''}name`,
      }),
    );
  };

  const handleSearch = (value: string) => {
    setLoading(true);

    dispatch(
      getHRControlListThunk({
        q: value,
        page: 1,
        sort: `${sort === SortTypes.DESC ? '-' : ''}name`,
      }),
    ).finally(() => setLoading(false));
  };

  const debounceSearchValue = useCallback(
    debounce(handleSearch, 300) as Function,
    [],
  );

  const handleSort = (sortOrder: SortTypes) => {
    setSort(sortOrder === SortTypes.ASC ? SortTypes.DESC : SortTypes.ASC);
  };

  const updateItems = (items: any[]) => {
    dispatch(setHrControlList(items));
  };

  useEffect(() => {
    dispatch(getHRControlListThunk(fetchParams));
    loadVisibilitySettings();
  }, []);

  if (!viewable) return null;

  return (
    <CardComponent>
      <StyledControlSectionContainer>
        <StyledControlSectionHeader>
          <StyledHeaderTitle>
            <StyledHeaderLabel>{t('HR management')}</StyledHeaderLabel>

            <StyledSortContainer onClick={() => handleSortChange(sort)}>
              <SortArrowsIcon sortOrder={sort} svgId={sectionType} />
            </StyledSortContainer>
          </StyledHeaderTitle>
        </StyledControlSectionHeader>

        <StyledSearchWrapper>
          <InputComponent
            value={searchQuery}
            onChange={(searchValue: string) => {
              setSearchQuery(searchValue);
              debounceSearchValue(searchValue);
            }}
            onEnter={handleSearch}
            placeholder={t(`Search ${sectionType}`)}
            right={
              <StyledSearchIcon>
                {isLoading ? <SpinnerComponent size="small" /> : <SearchIcon />}
              </StyledSearchIcon>
            }
          />
        </StyledSearchWrapper>

        <ControlSectionContent
          isLoadMode={!(pagination?.currentPage === pagination?.lastPage)}
          initialData={hrs}
          fetchData={getHRControlListThunk}
          sectionType={sectionType}
          fetchParams={{
            q: searchQuery,
            sort: `${sort === SortTypes.DESC ? '-' : ''}name`,
            page: pagination?.currentPage || 1,
          }}
          updateItems={updateItems}
          renderItem={item => {
            const enrichedItem = enrichHrItem(item);

            return (
              <StyledControlListItem
                key={item.id}
                highlighted={item.isAllRegions}
                onClick={() =>
                  openModal(ControlCreateEditModal, {
                    sectionType,
                    sectionItem: enrichedItem,
                    editSectionItem,
                    editable,
                    deletable,
                    canManageCandidateVisibility,
                    onSaveVisibility: saveVisibility,
                  })
                }
              >
                <ControlHRItem
                  item={enrichedItem}
                  showVisibilityDate={canManageCandidateVisibility}
                />
              </StyledControlListItem>
            );
          }}
        />
      </StyledControlSectionContainer>
    </CardComponent>
  );
};
